// NPM Imports

// Internal Imports
import { COMMON, GRID } from "../../constants";
import {
  REACTION_ON_CELL_CHANGE,
  REACTION_ON_ADDING_PRODUCTS_INTO_DEALTABLE,
  HANDLE_CELL_EDITABILITY,
} from "../constants";
import { ProgramUtils } from "../../utils";

// Service Imports
import {
  addItemsToDealTable,
  createDealTableGroup,
  getDealTableDiscount,
  saveDealTableRow,
  updateDealTableGroup,
  updateOverallDealDiscount,
} from "../../actions/DealTableActions/DealTableServices";

// Method Imports
import {
  convertCustomToRawRowData,
  convertRawToCustomRowData,
} from "../methods";

// Destructuring
const { DEALTABLE_COLUMN_TYPES, DEALTABLE_COLUMNS, PRICING_METHODS } = GRID;
const { TABLE_TYPE } = COMMON;
const { roundUpByDecimal, roundUpByNumber, roundUpByUnit } = ProgramUtils;

const COLUMNS_PINNED_TO_LEFT = [
  DEALTABLE_COLUMNS.PRODUCT_NAME,
  DEALTABLE_COLUMNS.PRODUCT_DESC,
];

/**
 * * INDEX
 * - generateColumnDef
 * - generateDiscountReduxData
 * - generateGroupData
 * - generateMetaDataForTable
 * - generateRowData
 * - generateRowDataFromAPI
 * - generateRowRippleEffects
 * - handleIsBasePriceDynamic
 * - handleIsCellEditable
 * - handleIsCellHasMinValue
 * - handleIsCellPopUp
 * - handleNetPriceRounding
 * - handlePercentageOfTotal
 * - mapProductsToDealTable
 * - roundUpNetPrice
 * - saveDealTableRows
 * - updateDealDiscountData
 * - updateDealDiscountToBackEnd
 * - updateDealTableRows
 * - updateGroupData
 * - updateProductCatalogFlags
 */

/**
 * * Resturcture backend API JSON to support revvGrid column syntax
 * @param {object} payload -
 * @param {object} rawColumnData -
 */
export const generateColumnDef = ({ readOnlyData, payload }) => {
  const {
    rawColumnData: { VIW: { columns = [], columnOrder = [] } = {} } = {},
  } = readOnlyData;
  columns.forEach(item => {
    const obj = {}; // This will hold the final properties of each column
    obj.columnId = item.id;
    obj.draggable = "true";
    obj.editable = item.editable;
    obj.field = item.field.id;
    obj.hidden = item.hidden;
    obj.visible = item.hidden;
    obj.headerName = item.label;
    obj.minWidth = 90;
    obj.width = 90;
    obj.name = item.name;
    obj.pinned = COLUMNS_PINNED_TO_LEFT.includes(item.name);
    obj.position = (columnOrder && columnOrder.indexOf(obj.name) + 1) || 100;
    obj.props = item.field.props;
    obj.sortStatus = "";
    obj.type =
      item.name === "price"
        ? "CURRENCY_FIELD"
        : DEALTABLE_COLUMN_TYPES[item.field.type]; // Temp type handle until BackEnd supports
    obj.visible = item.hidden || !item.visible || false;
    payload.columnDefs.push(obj);
  });

  /** In the above forEach func, the loop is done based on the columns provided by the API,
   * now when displaying, the columns have to be displayed in a specific order, to achieve this
   * we have used "position" key which will set a number like "6,2,etc" which defines the order position.
   * Now we need to sort them in ascending order.
   */
  payload.columnDefs.sort((a, b) => a.position - b.position);
};

// ======================================================================

/**
 *
 */
export const generateDiscountReduxData = ({ payload }) => {
  const { discount } = payload;
  if (discount.groups && discount.groups.length > 0) {
    discount.group_by = discount.groups[0].group_by.toLowerCase();
    discount.groups.forEach(group => {
      const discountAmount = (group.group_total * group.discount_percent) / 100;
      // Add Custom Keys into redux to be displayed in the group title component
      group.discountAmount = discountAmount;
      group.discountedGroupTotal = group.group_total - discountAmount;
    });
  }
};

// ======================================================================

/**
 *
 *
 */
export const generateGroupData = async ({ readOnlyData, payload }) => {
  try {
    const {
      dealTableId,
      metaData: { fieldMapIndex },
    } = readOnlyData;
    const { discount, rowData } = payload;

    if (discount.groups && discount.groups.length > 0) {
      const netPriceFld = fieldMapIndex[DEALTABLE_COLUMNS.NET_PRICE];
      const columnName = discount.groups[0].group_by.toLowerCase();
      const groupByOptions = [
        ...new Set(
          payload.rowData.map(
            row => row.rowData[fieldMapIndex[columnName]].cellData,
          ),
        ),
      ];
      groupByOptions.forEach(groupByValue => {
        const netPriceArray = rowData
          .filter(
            row =>
              row.rowData[fieldMapIndex[columnName]].cellData === groupByValue,
          )
          .map(row => row.rowData[netPriceFld].cellData);
        const sumOfRows = netPriceArray.reduce((a, b) => a + b, 0);
        const groupData = discount.groups.find(
          group => group.group_by_value === groupByValue,
        );
        const discountAmount =
          (sumOfRows * ((groupData && groupData.discount_percent) || 0)) / 100;
        if (!groupData) {
          const groupPayload = {
            group_by: columnName.toUpperCase(),
            group_by_value: groupByValue,
            discount_percent: 0,
            group_total: sumOfRows,
            discountAmount: 0,
            discountedGroupTotal: sumOfRows,
            isDirty: true,
          };
          discount.groups.push(groupPayload);
        } else {
          groupData.group_total = sumOfRows;
          groupData.discountAmount = discountAmount;
          groupData.discountedGroupTotal = sumOfRows - discountAmount;
        }
      });

      let addNewGroups = null;
      let updateExistingGroups = null;

      const newlyCreatedGroups = discount.groups.filter(group => !group.id);
      const mutatedExistingGroups = discount.groups.filter(group => group.id);

      if (mutatedExistingGroups && mutatedExistingGroups.length > 0) {
        const updatedGroupsPayload = { groups: mutatedExistingGroups };
        updateExistingGroups = await updateDealTableGroup(
          dealTableId,
          updatedGroupsPayload,
        );
      }

      if (newlyCreatedGroups && newlyCreatedGroups.length > 0) {
        const newlyCreatedGroupsPayload = { groups: newlyCreatedGroups };
        addNewGroups = await createDealTableGroup(
          dealTableId,
          newlyCreatedGroupsPayload,
        );
      }

      await Promise.all([updateExistingGroups, addNewGroups])
        .then(response => {
          if (response[1] && response[1].Deal) {
            payload.discount = response[1].Deal;
          }
        })
        .catch(error => {
          console.error("DealTableActions => onDealTableSave", error);
          throw error;
        });
    }
  } catch (err) {
    console.error("Mutations => generateGroupData", err);
  }
};

// ======================================================================

/**
 *
 * @param {object} payload -
 * @param {object} rawColumnData -
 */
export const generateMetaDataForTable = ({ readOnlyData, payload }) => {
  const { rawColumnData } = readOnlyData;
  const tblId = rawColumnData.VIW.tableId; // Referring to Dealtable ID.
  const {
    apiRequiredFields,
    derivedColumn,
    fieldMapIndex,
    linkedTables,
  } = payload.metaData;

  rawColumnData.VIW.columns.forEach(item => {
    /**
     * Since the column names are dynamic and sent as FLDXXX from backend it will
     * create a problem when we need to target a specific column, he we create a map
     * which points the filed name to its respective FLDXXX key => {sku: FLDxWnEYKHlO4zM}
     */
    fieldMapIndex[item.name] = item.field.id;

    /**
     * Now we need to seperate the columns belonging to the same table and linked table.
     * "apiRequiredFields" => Hold the column FLDXXX belonging to same table
     * "linkedTables" => Holds linked table ID and its FLDXXX as arrayOfObjects
     */
    if (item.field.tblId === tblId) {
      apiRequiredFields.push(item.field.id);
    } else {
      const isTblExists = linkedTables.find(
        tbl => tbl.tblId === item.field.tblId,
      );
      if (isTblExists) {
        isTblExists.ids.push(item.field.id); // If the Table is already created then push the field(FLDXXX) into "ids" key
      } else {
        linkedTables.push({
          tblId: item.field.tblId,
          ids: [item.field.id],
        }); // Create an array to store linked table id's & field id's
      }
    }

    /**
     * Filter out the derived columns, needed in dealtable
     */
    if (item.type === "DerivedColumn") {
      derivedColumn.sourceField = item.field.props.sourceField;
      derivedColumn.sourceTable = item.props.sourceTable;
    }

    /**
     * Product catalog search in Dealtable page, has a product type dropdown, for this the
     * options are generated from column api metadata.
     */
    const columnNames = [
      DEALTABLE_COLUMNS.CONFIG_TYPE,
      DEALTABLE_COLUMNS.PTYPE,
    ];
    if (columnNames.includes(item.name)) {
      payload.props[item.name] = item.field.props.values;
    }
  });
};

// ======================================================================

/**
 *
 */
export const generateRowData = ({ readOnlyData, payload }) => {
  const { rawRowData, tableType } = readOnlyData;
  const {
    addedCatalogIds,
    metaData: {
      linkedTables,
      fieldMapIndex,
      derivedColumn: { sourceTable, sourceField },
    },
  } = payload;
  const parsedRow = convertRawToCustomRowData(
    addedCatalogIds,
    fieldMapIndex,
    linkedTables,
    rawRowData,
    sourceField,
    sourceTable,
  );
  payload.rowData = parsedRow;

  // HANDLE QUOTE TABLE LOGIC
  if (tableType === TABLE_TYPE.QUOTE_TABLE) {
    payload.break = true;
  }
};

// ======================================================================

/**
 *
 */
export const generateRowDataFromAPI = async ({ readOnlyData, payload }) => {
  try {
    // Declarations
    const dealItems = [];
    let groupData = null;

    // Destructure
    const {
      dealTableId,
      documentId,
      discount,
      metaData: {
        derivedColumn: { sourceTable, sourceField },
        fieldMapIndex,
        linkedTables,
      },
      pricelistId,
      productIds,
      roundingType,
      rowData,
    } = readOnlyData;
    const { catalogIds } = payload;
    const groupedBy =
      discount.groups && discount.groups.length > 0
        ? discount.groups[0].group_by
        : "";
    // Pass current dealtable data to backend to do calcualtion for POT and Grouping Logics
    rowData.forEach(row => {
      const extBasePrice =
        row.rowData[fieldMapIndex[DEALTABLE_COLUMNS.EXT_BASE_PRICE]].cellData;
      const netPrice =
        row.rowData[fieldMapIndex[DEALTABLE_COLUMNS.NET_PRICE]].cellData;
      dealItems.push({
        price_item_id: row.rowMeta.index,
        ebp: extBasePrice,
        net_price: netPrice,
        is_pot:
          row.rowMeta.pricingData.type === PRICING_METHODS.PERCENTAGE_OF_TOTAL,
      });
    });

    const savedRows = await addItemsToDealTable(
      dealTableId,
      documentId,
      pricelistId,
      productIds,
      groupedBy,
      dealItems,
      roundingType,
    );
    if (groupedBy) {
      groupData = await getDealTableDiscount(dealTableId);
    }

    return await Promise.all([savedRows, groupData])
      .then(response => {
        const rowForFrontEnd = convertRawToCustomRowData(
          catalogIds,
          fieldMapIndex,
          linkedTables,
          response[0].RowRecords,
          sourceField,
          sourceTable,
        );
        payload.rowData = rowForFrontEnd;
        payload.discount.groups =
          (response[1] && response[1].Groups) || discount.groups;
      })
      .catch(error => {
        console.error("Mutations => generateRowDataFromAPI", error);
        throw error;
      });
  } catch (error) {
    console.error("Mutations => generateRowDataFromAPI", error);
    throw error;
  }
};

// ======================================================================

/**
 *
 */
export const generateRowRippleEffects = ({ readOnlyData, payload }) => {
  const { methods = [] } =
    REACTION_ON_CELL_CHANGE[readOnlyData.columnName] || {};
  if (methods.length > 0) {
    methods.forEach(rule => {
      if (!payload.break) {
        return rule({ readOnlyData, payload });
      }
      return false;
    });
  }
};

// ======================================================================

/**
 *
 */
export const handleIsBasePriceDynamic = ({ payload, readOnlyData }) => {
  const { metaData } = readOnlyData;
  const { initialGridLoadFlag, rowData } = payload;

  // To Handle initial load method & rest of the actions in dealtable
  let fieldMapIndex = {};
  if (initialGridLoadFlag) {
    ({ fieldMapIndex } = payload.metaData);
  } else {
    ({ fieldMapIndex } = metaData);
  }

  rowData.forEach(row => {
    let isBasePriceDynamic = false;
    if (
      row.rowMeta.pricingData.isAdhoc ||
      row.rowMeta.pricingData.type === PRICING_METHODS.STEP_TIER_PRICING ||
      row.rowMeta.pricingData.type === PRICING_METHODS.TIER_PRICING ||
      row.rowMeta.pricingData.type === PRICING_METHODS.VOLUME_PRICING
    ) {
      isBasePriceDynamic = true;
    }
    row.rowData[
      fieldMapIndex[DEALTABLE_COLUMNS.PRICE]
    ].cellMeta.isBasePriceDynamic = isBasePriceDynamic;
  });
};

// ======================================================================

/**
 *
 */
export const handleIsCellEditable = ({ payload, readOnlyData }) => {
  const { metaData } = readOnlyData;
  const { initialGridLoadFlag, rowData } = payload;

  // To Handle initial load method & rest of the actions in dealtable
  let fieldMapIndex = {};
  if (initialGridLoadFlag) {
    ({ fieldMapIndex } = payload.metaData);
  } else {
    ({ fieldMapIndex } = metaData);
  }

  rowData.forEach(row => {
    Object.keys(HANDLE_CELL_EDITABILITY).forEach(key => {
      row.rowData[
        fieldMapIndex[key]
      ].cellMeta.editable = HANDLE_CELL_EDITABILITY[key].method({
        fieldMapIndex,
        row,
      });
    });
  });
};

// ======================================================================

/**
 *
 */
export const handleIsCellHasMinValue = ({ payload, readOnlyData }) => {
  const { metaData } = readOnlyData;
  const { initialGridLoadFlag, rowData } = payload;

  // To Handle initial load method & rest of the actions in dealtable
  let fieldMapIndex = {};
  if (initialGridLoadFlag) {
    ({ fieldMapIndex } = payload.metaData);
  } else {
    ({ fieldMapIndex } = metaData);
  }

  rowData.forEach(row => {
    // SET MINIMUM VALUE FOR SELLING TERM
    row.rowData[
      fieldMapIndex[DEALTABLE_COLUMNS.SELLING_TERM]
    ].cellMeta.minValue =
      row.rowData[fieldMapIndex[DEALTABLE_COLUMNS.SELLING_TERM]].cellData || 1;

    // SET MINIMUM VALUE FOR QTY
    row.rowData[fieldMapIndex[DEALTABLE_COLUMNS.QUANTITY]].cellMeta.minValue =
      row.rowData[fieldMapIndex[DEALTABLE_COLUMNS.MIN_QTY]].cellData || 1;

    // SET MINIMUM VALUE FOR EXT_BASE_PRICE WHEN PRICING TYPE IS DIY
    if (row.rowMeta.pricingData.type === PRICING_METHODS.DIY_PRICING) {
      row.rowData[
        fieldMapIndex[DEALTABLE_COLUMNS.EXT_BASE_PRICE]
      ].cellMeta.minValue = row.rowMeta.pricingData.minPrice;
    }
  });
};

// ======================================================================

/**
 *
 */
export const handleIsCellPopUp = ({ payload, readOnlyData }) => {
  const { metaData } = readOnlyData;
  const { initialGridLoadFlag, rowData } = payload;

  // To Handle initial load method & rest of the actions in dealtable
  let fieldMapIndex = {};
  if (initialGridLoadFlag) {
    ({ fieldMapIndex } = payload.metaData);
  } else {
    ({ fieldMapIndex } = metaData);
  }

  rowData.forEach(row => {
    let isPopUp = false;
    if (
      row.rowMeta.pricingData.isAdhoc ||
      row.rowMeta.pricingData.type === PRICING_METHODS.STEP_TIER_PRICING ||
      row.rowMeta.pricingData.type === PRICING_METHODS.TIER_PRICING ||
      row.rowMeta.pricingData.type === PRICING_METHODS.VOLUME_PRICING
    ) {
      isPopUp = true;
    }
    row.rowData[
      fieldMapIndex[DEALTABLE_COLUMNS.QUANTITY]
    ].cellMeta.isPopUp = isPopUp;
  });
};

// ======================================================================

/**
 *
 */
export const handlePercentageOfTotal = ({ readOnlyData, payload }) => {
  const {
    metaData: { fieldMapIndex },
  } = readOnlyData;
  const { rowData = {} } = payload;

  //* Filter out the products which are percentageOfTotalProduct
  const percentageOfTotalProduct = rowData.filter(
    row => row.rowMeta.pricingData.type === PRICING_METHODS.PERCENTAGE_OF_TOTAL,
  );

  if (percentageOfTotalProduct.length > 0) {
    percentageOfTotalProduct.forEach(product => {
      product.rowMeta.isDirty = true; // Save only the dirty rows to backend

      const {
        destinationColumn,
        sourceColumn,
        value,
      } = product.rowMeta.pricingData;

      //* Filter out the product Ids which are mapped to percentageOfTotalProduct
      const potProductIds =
        product.rowMeta.pricingData.priceListItems &&
        product.rowMeta.pricingData.priceListItems.map(item => item.product_id);

      //* Perform POT logic only if items are present
      if (potProductIds) {
        //* Filter out the products which are mapped to percentageOfTotalProduct
        const productsAttachedToPOT = rowData.filter(row =>
          potProductIds.includes(
            row.rowData[fieldMapIndex[DEALTABLE_COLUMNS.PRODUCT]].cellData,
          ),
        );

        const totalValue = productsAttachedToPOT.reduce(
          (total, row) =>
            total + row.rowData[fieldMapIndex[sourceColumn]].cellData,
          0,
        );
        const index = rowData.findIndex(
          item => item.rowMeta.index === product.rowMeta.index,
        );

        // Update the column value of the row
        const extBasePrice = (totalValue * value) / 100;
        rowData[index].rowData[
          fieldMapIndex[destinationColumn]
        ].cellData = extBasePrice;

        // Side effect handling
        const discountPercentage =
          rowData[index].rowData[fieldMapIndex[DEALTABLE_COLUMNS.DISC_PERCENT]]
            .cellData;
        const discountedPrice = extBasePrice * (1 - discountPercentage / 100);
        const addtionalDiscountPercentage =
          rowData[index].rowData[
            fieldMapIndex[DEALTABLE_COLUMNS.ADDITIONAL_DISCOUNT_PERCENT]
          ].cellData;

        // Update discounted price
        rowData[index].rowData[
          fieldMapIndex[DEALTABLE_COLUMNS.DISCOUNTED_PRICE]
        ].cellData = extBasePrice * (1 - discountPercentage / 100);

        // Update net price
        rowData[index].rowData[
          fieldMapIndex[DEALTABLE_COLUMNS.NET_PRICE]
        ].cellData =
          discountedPrice -
          (discountedPrice * addtionalDiscountPercentage) / 100;
      }
    });
  } else {
    payload.stopRoundUpReCalc = true;
  }
};

// ======================================================================

/**
 *
 */
export const mapProductsToDealTable = ({ readOnlyData, payload }) => {
  const { rowData } = payload;
  const {
    metaData: { fieldMapIndex },
    productIds,
  } = readOnlyData;
  const columns = REACTION_ON_ADDING_PRODUCTS_INTO_DEALTABLE;

  // * Filter out the products to be added into dealtable from product catalog list
  payload.productCatalogs.forEach(item => {
    if (productIds.includes(item.id)) {
      const obj = {
        rowData: {},
        rowMeta: {
          index: item.id, // Holds the product id,
          editable: false,
          pricingData: item.price,
        },
      };

      // Map product flat values to FLD values
      Object.keys(columns).forEach(key => {
        const {
          properties,
          properties: {
            type = "",
            methods = [],
            lookUp = "",
            defaultValue = "",
          },
        } = columns[key];
        switch (type) {
          case "LOOKUP":
            {
              // Temp Code to be changed later
              const propertyKey = lookUp.split(".");
              const value =
                propertyKey.length === 2
                  ? item[propertyKey[0]][propertyKey[1]]
                  : item[propertyKey[0]];

              if (value || value === 0) {
                obj.rowData[fieldMapIndex[key]] = {
                  cellData: value,
                  cellMeta: {},
                };
              } else {
                obj.rowData[fieldMapIndex[key]] = {
                  cellData: defaultValue,
                  cellMeta: {},
                };
              }
            }
            break;
          case "FUNCTION":
            {
              const value = methods.map(rule =>
                rule({ data: item, readOnlyData, payload, properties }),
              );
              obj.rowData[fieldMapIndex[key]] = {
                cellData: value[value.length - 1],
                cellMeta: {},
              };
            }
            break;
          case "DEFAULT":
            obj.rowData[fieldMapIndex[key]] = {
              cellData: defaultValue,
              cellMeta: {},
            };
            break;
          default:
            break;
        }
        payload.row = obj; // Need this key, to hold current row data to be used inside methods.
      });
      rowData.push(obj);
    }
  });

  payload.row = {}; // Reset back to {}, as its a temp storage for the pre-calculations
};

// ======================================================================

export const saveDealTableRows = async ({ readOnlyData, payload }) => {
  try {
    const {
      dealTableId,
      metaData: {
        apiRequiredFields,
        derivedColumn: { sourceTable, sourceField },
        fieldMapIndex,
        linkedTables,
        masterTblID,
        tableId,
        tblViewId,
      },
    } = readOnlyData;
    const { rowData = [], catalogIds = [] } = payload;

    const rowForBackEnd = convertCustomToRawRowData(rowData, apiRequiredFields);
    const savedRows = await saveDealTableRow(
      dealTableId,
      rowForBackEnd,
      masterTblID,
      tableId,
      tblViewId,
    );
    const rowForFrontEnd = convertRawToCustomRowData(
      catalogIds,
      fieldMapIndex,
      linkedTables,
      savedRows.RowRecords,
      sourceField,
      sourceTable,
    );

    payload.rowData = rowForFrontEnd;
  } catch (err) {
    console.error("Mutations => saveDealTableRows", err);
  }
};

// ======================================================================

export const roundUpNetPrice = ({ payload, readOnlyData }) => {
  const {
    roundingType,
    metaData: { fieldMapIndex },
  } = readOnlyData;
  const { rowData } = payload;
  const netPriceFld = fieldMapIndex[DEALTABLE_COLUMNS.NET_PRICE];
  // Update the net amount value based on the round Off type selection
  rowData.forEach(row => {
    switch (roundingType) {
      case "decimal":
        row.rowData[netPriceFld].cellData = roundUpByDecimal(
          row.rowData[netPriceFld].cellData,
          1,
        );
        break;
      case "unit":
        row.rowData[netPriceFld].cellData = roundUpByUnit(
          row.rowData[netPriceFld].cellData,
        );
        break;
      case "tens":
        row.rowData[netPriceFld].cellData = roundUpByNumber(
          row.rowData[netPriceFld].cellData,
          10,
        );
        break;
      default:
        break;
    }
  });
};

// ======================================================================

/**
 *
 */
export const handleNetPriceRounding = ({ payload, readOnlyData }) => {
  const { roundingType } = readOnlyData;
  const { stopRoundUpReCalc } = payload;

  if (!stopRoundUpReCalc && roundingType) {
    roundUpNetPrice({ payload, readOnlyData });
  }
};

// ======================================================================

/**
 *
 */
export const updateDealDiscountData = ({ readOnlyData, payload }) => {
  const { metaData } = readOnlyData;
  const { discount, initialGridLoadFlag, rowData } = payload;

  // To Handle initial load method & rest of the actions in dealtable
  let fieldMapIndex = {};
  if (initialGridLoadFlag) {
    ({ fieldMapIndex } = payload.metaData);
  } else {
    ({ fieldMapIndex } = metaData);
  }

  const netPriceFld = fieldMapIndex[DEALTABLE_COLUMNS.NET_PRICE];
  let dealTotal = 0;

  if (discount.groups && discount.groups.length > 0) {
    dealTotal = discount.groups
      .map(group => group.discountedGroupTotal)
      .reduce((a, b) => a + b, 0);
  } else {
    const netPrices = rowData.map(row => row.rowData[netPriceFld].cellData);
    dealTotal = netPrices.length > 0 ? netPrices.reduce((a, b) => a + b, 0) : 0;
  }

  const discountAmount = (dealTotal * discount.deal_discount) / 100;
  discount.dealTotal = dealTotal;
  discount.discountAmount = discountAmount;
  discount.discountedDealTotal = dealTotal - discountAmount;
};

// ======================================================================

/**
 *
 */
export const updateDealDiscountToBackEnd = async ({
  readOnlyData,
  payload,
}) => {
  const { dealTableId } = readOnlyData;
  const { discount } = payload;
  try {
    const updatedDealDiscount = await updateOverallDealDiscount(dealTableId, {
      deal_discount: parseFloat(discount.deal_discount),
      total_price: discount.dealTotal,
      net_price: discount.discountedDealTotal,
    });
    console.debug(updatedDealDiscount);
  } catch (err) {
    console.error("Mutations => saveDealTableRows", err);
  }
};

// ======================================================================

/**
 *
 */
export const updateDealTableRows = async ({ readOnlyData, payload }) => {
  const {
    dealTableId,
    metaData: { apiRequiredFields, masterTblID, tableId, tblViewId },
  } = readOnlyData;
  const { rowData = [] } = payload;

  const dirtyRows = rowData.filter(row => row.rowMeta.isDirty);
  const rowForBackEnd = convertCustomToRawRowData(dirtyRows, apiRequiredFields);
  try {
    const savedRows = await saveDealTableRow(
      dealTableId,
      rowForBackEnd,
      masterTblID,
      tableId,
      tblViewId,
    );
    console.debug(savedRows);
    // Reset the dirty flag
    rowData.forEach(row => {
      delete row.rowMeta.isDirty;
    });
  } catch (err) {
    console.error("Mutations => updateDealTableRows", err);
  }
};

// ======================================================================

/**
 *
 *
 */
export const updateGroupData = async ({ readOnlyData, payload }) => {
  const {
    dealTableId,
    metaData: { fieldMapIndex },
  } = readOnlyData;
  const { discount, rowData } = payload;
  if (discount.groups && discount.groups.length > 0) {
    const netPriceFld = fieldMapIndex[DEALTABLE_COLUMNS.NET_PRICE];
    const groupBy = discount.groups[0].group_by.toLowerCase();
    const groupByValue = payload.row.rowData[fieldMapIndex[groupBy]].cellData;
    const groupToUpdate = discount.groups.find(
      group => group.group_by_value === groupByValue,
    );
    const filteredGroupByRows = rowData
      .filter(
        row => row.rowData[fieldMapIndex[groupBy]].cellData === groupByValue,
      )
      .map(row => row.rowData[netPriceFld].cellData);
    const groupTotal = filteredGroupByRows.reduce((a, b) => a + b, 0);
    const discountAmount = (groupTotal * groupToUpdate.discount_percent) / 100;
    groupToUpdate.group_total = groupTotal;
    groupToUpdate.discountAmount = discountAmount;
    groupToUpdate.discountedGroupTotal = groupTotal - discountAmount;

    try {
      const updatedGroupData = await updateDealTableGroup(dealTableId, {
        groups: [groupToUpdate],
      });
      console.debug(updatedGroupData);
    } catch (err) {
      console.error("Mutations => saveDealTableRows", err);
    }
  }
};

// ======================================================================

/**
 * "updateProductCatalogFlags" func updates the isChecked/isDisabled flag of a single/multiple product data.
 * @param {boolean} flag - Holds true/false value to set in flagType.
 * @param {string} flagType - Key which the flag value gets added to, like "isChecked & isDisabled".
 * @param {arrayOfObj} productCatalogs - Holds the entire product catalog list.
 * @param {number} productId(s) - Points to the product id // 15
 */
export const updateProductCatalogFlags = ({ readOnlyData, payload }) => {
  const { flag, flagType, productCatalogs, productIds } = readOnlyData;
  // Update the flags inside the product data
  payload.productCatalogs = productCatalogs.map(item => {
    if (productIds.includes(item.id)) {
      if (!flagType) {
        return { ...item, isDisabled: flag, isChecked: flag };
      }
      return { ...item, [flagType]: flag };
    }
    return item;
  });
};

// ======================================================================



// WEBPACK FOOTER //
// ./src/mashupV4/mutations/index.js